import { type FC, type ReactElement, forwardRef, useMemo, lazy, Suspense } from 'react';
import { optionalProp } from '../core';
import { MLPVersion } from '../types/enums';
import { filterSearchIgnoreCase } from '../utils';
import type {
  DropDownPropsType,
  WrapperPropTypes,
  Data as DropDownDataInterface,
  DropDownMultiPropsType,
  DropDownTheme,
  DropDownInlinePropsType,
  Data as FilterData,
} from './types/propsType';
import { DropDownType, DropDownValidations, DropDownFilterBy } from './types/enums';
import styles from './DropDown.module.scss';
const DropDownMLP1 = lazy(() => import('./mlp1/DropDownMLP1'));
const DropDownMLP2 = lazy(() => import('./mlp2/DropDownMLP2'));
const MultiSelect = lazy(() => import('./multiSelect/MultiSelect'));
const InlineDropDown = lazy(() => import('./inline/inlineDropDown'));

/**
 * @description DropDown component provides select options in <DropDown></DropDown> with basic styling
 * Props ->
 * className: prop to get custom styling class
 * value: display value of the dropdown
 * dropdownLabel: prop to add field label above text area
 * status: defines status of entry if any i.e. default,valid or invalid
 * value: defines the selected option from options populated
 * disabled: prop to set disabled or enabled selection
 *
 * mlpVersion: (Default = 2) defines different availabel version of DropDown Component.
 */

const DropDown: FC<WrapperPropTypes> = forwardRef<HTMLSpanElement, WrapperPropTypes>(
  (
    {
      mlpVersion = MLPVersion.TWO,
      multiSelection,
      dropDownType = DropDownType.DEFAULT,
      drawerParentOutside = false,
      filterBy = DropDownFilterBy.INCLUDES,
      isWithinTable,
      ...restProps
    }: WrapperPropTypes,
    ref,
  ): ReactElement => {
    const { MULTI_SELECT, INLINE } = DropDownType;
    const localDropDownType = multiSelection ? MULTI_SELECT : dropDownType;

    const props = restProps as DropDownPropsType;

    const getFilterValue = () => {
      if (props.applySearchFilter === true && props.searchFilterValue) {
        return props.searchFilterValue;
      } else if ((mlpVersion === MLPVersion.TWO && !props.disabledFilter) || props.isCountrySelector === true) {
        return props.value;
      } else {
        return '';
      }
    };
    const filteredData = useMemo(
      () =>
        getFilterValue()
          ? props.data?.filter((item: FilterData) =>
              filterSearchIgnoreCase(`${item[`${props.configData?.label}`]}`, getFilterValue() as string, filterBy),
            )
          : props.data,
      [props.data, props.value, props.searchFilterValue, filterBy],
    );

    const renderComponent = () => {
      switch (localDropDownType) {
        case MULTI_SELECT:
          return <MultiSelect {...(restProps as DropDownMultiPropsType)} />;

        case INLINE:
          return <InlineDropDown {...(restProps as DropDownInlinePropsType)} />;

        default: {
          const renderToProps = {
            ...(restProps as DropDownPropsType),
            ...optionalProp('data', filteredData),
            ...optionalProp('isWithinTable', isWithinTable),
          };
          if (mlpVersion === MLPVersion.ONE) {
            return <DropDownMLP1 {...renderToProps} />;
          }
          return <DropDownMLP2 {...renderToProps} />;
        }
      }
    };
    return (
      <span
        data-testid="dropdown-wrapper-test-id"
        ref={ref}
        id="nextgen-dropdown-component"
        style={{ position: drawerParentOutside ? 'static' : 'relative' }}
        className={styles['nextgen-dropdown-wrapper']}
      >
        <Suspense>{renderComponent()}</Suspense>
      </span>
    );
  },
);
DropDown.displayName = 'DropDown';
export { DropDownValidations, DropDownType };
export type { DropDownDataInterface, DropDownTheme };
export default DropDown;
